/**
 * Direct P-S Dialog — mirror Access popup `frmMatchingPSMainSubFormDirect`
 * (file frontend/plan/access_all/form_frmMatchingPSMainSubFormDirect.txt
 *  + spec frontend/plan/frmMatchingPSMainSubFormDirect_AI_Web_Conversion_Spec.md).
 *
 * Layout 4 vùng (spec §2.3):
 *   ┌─ Header: Match Date | Note ──────────────────────────────────────────┐
 *   ├─ P filter + grid (left) ───── S filter + grid (right) ────────────────┤
 *   ├─ Toolbar: [Save Plan] helper text ───────────────────────────────────┤
 *   └─ Detail grid (24 cols) + [Delete Plan] toolbar ──────────────────────┘
 *
 * Logic mirror VBA `btnSavePlan_Click` (spec §8-10):
 *   - ≥1 P + ≥1 S; multi-P + multi-S đồng thời reject.
 *   - matchKg < 0 reject; matchKg > pending reject; blank → fallback pending.
 *   - 1P → nS / nP → 1S min(remaining, pending) loop.
 * Backend re-validate qua POST /matching-ps/direct-ps/save.
 */
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TableVirtuoso } from 'react-virtuoso'

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'

import { useSnackbar } from '../../../hooks/useSnackbar'
import {
  useDeleteDirectPsPlanMutation,
  useGetDirectPsDetailsQuery,
  useGetMatchCategoriesQuery,
  useGetMatchPOverviewQuery,
  useGetMatchSOverviewQuery,
  useSaveDirectPsPlanMutation,
} from '../../../store/api/matchingPsApi'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  distinctColumnValues,
  filterBarBtnSx,
  FilterButton,
  FilterPopover,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  SearchFilterContent,
  useColumnFilters,
  useFilterPopover,
} from '../../../components/dataList'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortColumn,
  type TableSortState,
  toggleTableSort,
} from '../../../utils/tableSort'

/* ---------- formatters (đồng nhất với MatchInstructTab) ------------------- */

const ACCESS_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const

function fmtDate(value: unknown): string {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

function fmtNumber(value: unknown): string {
  if (value == null || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  const txt = Math.abs(Math.round(n)).toLocaleString('en-US')
  return n < 0 ? `(${txt})` : txt
}

function toNumber(v: unknown): number {
  if (v == null || v === '') return 0
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}

/* ---------- defaults ------------------------------------------------------ */

type DateRange = { from: string; to: string }
type Side = 'P' | 'S'

const ACCENT = { P: '#1e88e5', S: '#fb8c00' } as const

function defaultFiscalRange(now: Date = new Date()): DateRange {
  const y = now.getFullYear()
  const inQ4 = now.getMonth() >= 9
  const startYear = inQ4 ? y : y - 1
  const endYear = inQ4 ? y + 1 : y
  return {
    from: `${startYear}-10-01`,
    to: `${endYear}-09-30`,
  }
}

/* ---------- column defs --------------------------------------------------- */

interface CandidateCol {
  key: string
  label: string
  width: number
  kind?: 'date' | 'number' | 'text' | 'check' | 'editKg'
  align?: 'left' | 'right' | 'center'
  /** Override per-column filter kind. 'none' = bỏ icon ▽ (check/editKg). */
  filter?: ColumnFilterKind
}

// P-side column order theo Access spec frmMatchingPSMainSubFormDirect_p
// (Datasheet view sắp theo `Top` trong Form Design). Widths thu nhỏ tiếp,
// chỉ KG(EDIT) rộng hơn để fit format `1,234,567` với dấu phẩy.
const P_CANDIDATE_COLS: CandidateCol[] = [
  { key: 'checked_row', label: 'IsMatch?', width: 36, kind: 'check', align: 'center', filter: 'none' },
  { key: 'contractNo', label: 'ContractNo', width: 76 },
  { key: 'cpName', label: 'Supplier', width: 100 },
  { key: 'pPlanPending', label: 'Pending', width: 60, kind: 'number', align: 'right' },
  { key: 'match_kg', label: 'KG(EDIT)', width: 88, kind: 'editKg', filter: 'none' },
  { key: 'latestDelivery', label: 'LatestDelivery', width: 80, kind: 'date' },
  { key: 'deliveryPlace', label: 'Place', width: 96 },
  { key: 'qualityCode', label: 'Quality', width: 44 },
  { key: 'category', label: 'Grade', width: 44 },
  { key: 'quantityKg', label: 'ContractQuant', width: 72, kind: 'number', align: 'right' },
]

// S-side: align column order với P-side để user mắt scan ngang trái-phải
// đối xứng (IsMatch | Contract | Counterparty | Pending | KG(EDIT) | Date |
// Place | Quality | Grade | ContractQuant).
const S_CANDIDATE_COLS: CandidateCol[] = [
  { key: 'checked_row', label: 'IsMatch?', width: 36, kind: 'check', align: 'center', filter: 'none' },
  { key: 'contractNo', label: 'ContractNo', width: 76 },
  { key: 'cpName', label: 'Buyer', width: 100 },
  { key: 'sPlanPending', label: 'Pending', width: 60, kind: 'number', align: 'right' },
  { key: 'match_kg', label: 'KG(EDIT)', width: 88, kind: 'editKg', filter: 'none' },
  { key: 'deliveryFrom', label: 'ShipmentFrom', width: 80, kind: 'date' },
  { key: 'deliveryPlace', label: 'Place', width: 80 },
  { key: 'qualityCode', label: 'Quality', width: 44 },
  { key: 'category', label: 'Grade', width: 44 },
  { key: 'quantityKg', label: 'ContractQuant', width: 68, kind: 'number', align: 'right' },
]

// Shared header style cho cả candidate panels và detail table — CSS class
// `app-table-header-cell` chỉ apply gradient khi scoped trong `.app-dataframe`;
// wrapper đó force height clamp 420–1400px không dùng được trong dialog. Apply
// trực tiếp gradient + opaque bg + z-index để header sticky không bị data
// scroll xuyên qua (chồng chữ).
const HEADER_CELL_SX = {
  whiteSpace: 'nowrap' as const,
  position: 'sticky' as const,
  top: 0,
  zIndex: 5,
  backgroundColor: '#fff9f4',
  backgroundImage: 'linear-gradient(180deg, #fff9f4 0%, #f4df9c 100%)',
  color: '#0b3633',
  fontSize: '0.6rem',
  fontWeight: 900,
  lineHeight: 1.1,
  letterSpacing: '0.01em',
  borderTop: '1px solid rgba(11, 54, 51, 0.16)',
  borderBottom: '2px solid rgba(11, 54, 51, 0.34)',
  boxShadow: '0 2px 0 rgba(11, 54, 51, 0.08)',
  opacity: 1,
  py: 0.3,
  px: 0.3,
}

// Detail grid — 24 cột theo spec §3.4.
interface DetailCol {
  key: string
  label: string
  width: number
  kind?: 'date' | 'number'
  align?: 'left' | 'right'
  /** Override per-column filter kind (xem ColumnFilterKind). */
  filter?: ColumnFilterKind
}

const DETAIL_COLS: DetailCol[] = [
  { key: 'matchId', label: 'ID', width: 60, kind: 'number', align: 'right' },
  { key: 'matchDate', label: 'MatchDate', width: 100, kind: 'date' },
  { key: 'matchKg', label: 'MatchKG', width: 88, kind: 'number', align: 'right' },
  { key: 'sSide', label: 'S Contract', width: 100 },
  { key: 'sName', label: 'S Buyer', width: 130 },
  { key: 'sQuantity', label: 'S Quantity', width: 86, kind: 'number', align: 'right' },
  { key: 'sQuality', label: 'S Quality', width: 70 },
  { key: 'sDirectP', label: 'S Direct', width: 80, kind: 'number', align: 'right' },
  { key: 'sInstructBaoloc', label: 'S InstructBL', width: 88, kind: 'number', align: 'right' },
  { key: 'sInstructNhontrach', label: 'S InstructNT', width: 88, kind: 'number', align: 'right' },
  { key: 'sPlanPending', label: 'S Pending', width: 88, kind: 'number', align: 'right' },
  { key: 'sGrade', label: 'S Grade', width: 70 },
  { key: 'pSide', label: 'P Contract', width: 100 },
  { key: 'pName', label: 'P Supplier', width: 130 },
  { key: 'pQuantity', label: 'P Quantity', width: 86, kind: 'number', align: 'right' },
  { key: 'pQuality', label: 'P Quality', width: 70 },
  { key: 'pDirectS', label: 'P Direct', width: 80, kind: 'number', align: 'right' },
  { key: 'pInstructBaoloc', label: 'P InstructBL', width: 88, kind: 'number', align: 'right' },
  { key: 'pInstructNhontrach', label: 'P InstructNT', width: 88, kind: 'number', align: 'right' },
  { key: 'pPlanPending', label: 'P Pending', width: 88, kind: 'number', align: 'right' },
  { key: 'pGrade', label: 'P Grade', width: 70 },
  { key: 'note', label: 'Note', width: 140 },
  { key: 'dateUpdated', label: 'Updated Date', width: 130, kind: 'date' },
  { key: 'personUpdated', label: 'Updated By', width: 130 },
]

function formatDetailCell(row: Record<string, unknown>, col: DetailCol): string {
  const v = row[col.key]
  if (col.kind === 'date') return fmtDate(v)
  if (col.kind === 'number') return fmtNumber(v)
  if (v == null || v === '') return ''
  return String(v)
}

/* ---------- props --------------------------------------------------------- */

export interface DirectPSDialogProps {
  open: boolean
  onClose: () => void
  canEdit: boolean
}

export default function DirectPSDialog({ open, onClose, canEdit }: DirectPSDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { height: '95vh', maxHeight: '95vh' } }}
    >
      {open ? <DirectPSDialogBody onClose={onClose} canEdit={canEdit} /> : null}
    </Dialog>
  )
}

/* ---------- body (mounted on open để reset state mỗi lần mở) -------------- */

function DirectPSDialogBody({ onClose, canEdit }: { onClose: () => void; canEdit: boolean }) {
  const snack = useSnackbar()
  const fiscalDefault = useMemo(() => defaultFiscalRange(), [])
  const today = useMemo(() => dayjs().format('YYYY-MM-DD'), [])

  // Header inputs
  const [matchDate, setMatchDate] = useState(today)
  const [note, setNote] = useState('')

  // Filter state per side
  const [categoryP, setCategoryP] = useState('')
  const [rangeP, setRangeP] = useState<DateRange>(fiscalDefault)
  const [categoryS, setCategoryS] = useState('')
  const [rangeS, setRangeS] = useState<DateRange>(fiscalDefault)

  // Detail grid filter — mirror Access query_MatchingPSQryReportDirect_all
  // (match_date BETWEEN start/end AND p_grade = category). Default fiscal year
  // để giới hạn row scan; user có thể thu hẹp/đổi grade.
  const [rangeD, setRangeD] = useState<DateRange>(fiscalDefault)
  const [categoryD, setCategoryD] = useState('')

  // Selection state
  const [checkedP, setCheckedP] = useState<Set<string>>(new Set())
  const [checkedS, setCheckedS] = useState<Set<string>>(new Set())
  const [matchKgP, setMatchKgP] = useState<Record<string, string>>({})
  const [matchKgS, setMatchKgS] = useState<Record<string, string>>({})
  const [selectedDetailId, setSelectedDetailId] = useState<number | null>(null)

  // Detail table — client-side filter/sort layer (Grade/DateRange phía trên là
  // backend query, còn lớp này lọc trong rows đã load về). Skill /table-style.
  const [searchTermD, setSearchTermD] = useState('')
  const searchPopoverD = useFilterPopover<'search'>()
  const columnFiltersD = useColumnFilters()
  const [colPopoverD, setColPopoverD] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const [sortStateD, setSortStateD] = useState<TableSortState<string> | null>(null)

  /* ---------- data fetches ----------------------------------------------- */

  const categoryOptionsQuery = useGetMatchCategoriesQuery()
  const categoryOptions = categoryOptionsQuery.data ?? []

  const pQuery = useGetMatchPOverviewQuery({
    startDate: rangeP.from || undefined,
    endDate: rangeP.to || undefined,
    category: categoryP || undefined,
  })
  const sQuery = useGetMatchSOverviewQuery({
    startDate: rangeS.from || undefined,
    endDate: rangeS.to || undefined,
    category: categoryS || undefined,
  })
  const detailQuery = useGetDirectPsDetailsQuery({
    startDate: rangeD.from || undefined,
    endDate: rangeD.to || undefined,
    category: categoryD || undefined,
  })

  // Candidates = overview rows với pending > 0 (mirror Access tmp_match_*).
  const pRows = useMemo(
    () =>
      (pQuery.data?.items ?? []).filter(
        (r) => toNumber(r.pPlanPending) > 0,
      ) as Record<string, unknown>[],
    [pQuery.data],
  )
  const sRows = useMemo(
    () =>
      (sQuery.data?.items ?? []).filter(
        (r) => toNumber(r.sPlanPending) > 0,
      ) as Record<string, unknown>[],
    [sQuery.data],
  )
  const detailRows = useMemo(
    () => (detailQuery.data ?? []) as Record<string, unknown>[],
    [detailQuery.data],
  )

  // Apply global Search + per-column filters → sort. Skill /table-style: AND
  // 2 lớp filter, sort kế tiếp; TableVirtuoso render filteredDetailRows.
  const filteredDetailRows = useMemo(
    () =>
      detailRows.filter(
        (r) =>
          rowMatchesSearch(r, searchTermD, DETAIL_COLS)
          && rowMatchesAllColumnFilters(r, columnFiltersD.filters),
      ),
    [detailRows, searchTermD, columnFiltersD.filters],
  )
  const sortedDetailRows = useMemo(
    () =>
      sortTableRows(
        filteredDetailRows,
        sortStateD,
        DETAIL_COLS as ReadonlyArray<TableSortColumn<string>>,
      ),
    [filteredDetailRows, sortStateD],
  )

  const hasActiveDetailFilters = !!searchTermD.trim() || columnFiltersD.hasAny
  const activeDetailFilterCount =
    (searchTermD.trim() ? 1 : 0) + columnFiltersD.activeKeys.length
  const clearAllDetailFilters = () => {
    setSearchTermD('')
    columnFiltersD.clearAll()
    searchPopoverD.close()
  }

  const popoverDetailColumn = useMemo(
    () => (colPopoverD ? DETAIL_COLS.find((c) => c.key === colPopoverD.key) ?? null : null),
    [colPopoverD],
  )
  const popoverDetailKind = useMemo<ColumnFilterKind>(
    () =>
      popoverDetailColumn
        ? inferColumnFilterKind(popoverDetailColumn as never, detailRows)
        : 'text',
    [popoverDetailColumn, detailRows],
  )
  const popoverDetailOptions = useMemo(
    () =>
      popoverDetailKind === 'checkboxes' && colPopoverD
        ? distinctColumnValues(detailRows, colPopoverD.key)
        : undefined,
    [popoverDetailKind, colPopoverD, detailRows],
  )

  const toggleSortD = (key: string) =>
    setSortStateD((prev) => toggleTableSort(prev, key))

  /* ---------- mutations -------------------------------------------------- */

  const [savePlan, { isLoading: saving }] = useSaveDirectPsPlanMutation()
  const [deletePlan, { isLoading: deleting }] = useDeleteDirectPsPlanMutation()

  /* ---------- handlers --------------------------------------------------- */

  // Stable handlers — `KgEditCell` memo dùng reference-equality để skip
  // re-render. Inline arrow ở JSX (mỗi parent render → mới) sẽ bust memo
  // → toàn bộ KG TextField re-render mỗi keystroke = lag.
  const handleMatchKgP = useCallback((cn: string, value: string) => {
    setMatchKgP((prev) => ({ ...prev, [cn]: value }))
  }, [])
  const handleMatchKgS = useCallback((cn: string, value: string) => {
    setMatchKgS((prev) => ({ ...prev, [cn]: value }))
  }, [])

  // Toggle 1 row: tick → autofill matchKg = pending (user thấy giá trị ngay,
  // có thể sửa); untick → clear matchKg để không còn "rác". Capture
  // `wasChecked` qua closure inside setChecked updater để tránh stale state.
  const handleToggleP = useCallback((cn: string, pending: number) => {
    let wasChecked = false
    setCheckedP((prev) => {
      wasChecked = prev.has(cn)
      const next = new Set(prev)
      if (wasChecked) next.delete(cn)
      else next.add(cn)
      return next
    })
    setMatchKgP((prev) => {
      const next = { ...prev }
      if (wasChecked) delete next[cn]
      else next[cn] = String(Math.round(pending))
      return next
    })
  }, [])
  const handleToggleS = useCallback((cn: string, pending: number) => {
    let wasChecked = false
    setCheckedS((prev) => {
      wasChecked = prev.has(cn)
      const next = new Set(prev)
      if (wasChecked) next.delete(cn)
      else next.add(cn)
      return next
    })
    setMatchKgS((prev) => {
      const next = { ...prev }
      if (wasChecked) delete next[cn]
      else next[cn] = String(Math.round(pending))
      return next
    })
  }, [])

  // Toggle all: tick all → autofill matchKg cho TẤT CẢ base rows = pending;
  // untick all → clear sạch. Replace cả Set/Record thay vì merge (1 setState
  // thay vì N → tránh O(N²) spread khi 100+ rows).
  const handleToggleAllP = useCallback((allChecked: boolean) => {
    if (allChecked) {
      setCheckedP(new Set())
      setMatchKgP({})
    } else {
      const nextChecked = new Set<string>()
      const nextMk: Record<string, string> = {}
      pRows.forEach((r) => {
        const cn = String((r as Record<string, unknown>).contractNo ?? '')
        const pending = toNumber((r as Record<string, unknown>).pPlanPending)
        nextChecked.add(cn)
        nextMk[cn] = String(Math.round(pending))
      })
      setCheckedP(nextChecked)
      setMatchKgP(nextMk)
    }
  }, [pRows])
  const handleToggleAllS = useCallback((allChecked: boolean) => {
    if (allChecked) {
      setCheckedS(new Set())
      setMatchKgS({})
    } else {
      const nextChecked = new Set<string>()
      const nextMk: Record<string, string> = {}
      sRows.forEach((r) => {
        const cn = String((r as Record<string, unknown>).contractNo ?? '')
        const pending = toNumber((r as Record<string, unknown>).sPlanPending)
        nextChecked.add(cn)
        nextMk[cn] = String(Math.round(pending))
      })
      setCheckedS(nextChecked)
      setMatchKgS(nextMk)
    }
  }, [sRows])

  const buildPayloadRows = (
    side: Side,
  ): { contractNo: string; planPending: number; matchKg: number | null }[] => {
    const rows = side === 'P' ? pRows : sRows
    const checked = side === 'P' ? checkedP : checkedS
    const map = side === 'P' ? matchKgP : matchKgS
    const pendingKey = side === 'P' ? 'pPlanPending' : 'sPlanPending'
    return rows
      .filter((r) => checked.has(String(r.contractNo ?? '')))
      .map((r) => {
        const cn = String(r.contractNo ?? '')
        const raw = map[cn]
        const matchKg = raw == null || raw === '' ? null : Number(raw)
        return {
          contractNo: cn,
          planPending: toNumber(r[pendingKey]),
          matchKg: Number.isFinite(matchKg as number) ? (matchKg as number) : null,
        }
      })
  }

  const handleSave = async () => {
    if (!canEdit) {
      snack.error("You don't have permission for this action.")
      return
    }
    if (!matchDate) {
      snack.error('Match Date is required.')
      return
    }
    const purchaseRows = buildPayloadRows('P')
    const saleRows = buildPayloadRows('S')
    if (purchaseRows.length === 0 || saleRows.length === 0) {
      snack.warning('Please tick at least one P contract and at least one S contract!')
      return
    }
    const distinctP = new Set(purchaseRows.map((r) => r.contractNo)).size
    const distinctS = new Set(saleRows.map((r) => r.contractNo)).size
    if (distinctP > 1 && distinctS > 1) {
      snack.warning('Cannot select multiple P and multiple S contracts simultaneously!')
      return
    }
    // Client-side bound check — backend re-validate.
    for (const r of [...purchaseRows, ...saleRows]) {
      const qty = r.matchKg ?? r.planPending
      if (qty < 0) {
        snack.warning('Cannot input KG(EDIT) < 0 !')
        return
      }
      if (qty > r.planPending) {
        snack.warning('Cannot input KG(EDIT) > Pending !')
        return
      }
    }
    try {
      const res = await savePlan({
        matchDate,
        note,
        purchaseRows,
        saleRows,
      }).unwrap()
      snack.success(res.message || 'Successfully Updated')
      // Reset tick + matchKg; giữ filter để user thấy candidate refresh sau invalidate.
      setCheckedP(new Set())
      setCheckedS(new Set())
      setMatchKgP({})
      setMatchKgS({})
    } catch (err: unknown) {
      const msg =
        (err as { data?: { detail?: string } })?.data?.detail ??
        (err as { message?: string })?.message ??
        'Save failed'
      snack.error(msg)
    }
  }

  const handleDelete = async () => {
    if (!canEdit) {
      snack.error("You don't have permission for this action.")
      return
    }
    if (selectedDetailId == null) {
      snack.warning('Select a Direct P-S row below before clicking Delete Plan.')
      return
    }
    if (
      !window.confirm(`Do you want to delete plan with ID = '${selectedDetailId}'?`)
    ) {
      return
    }
    try {
      const res = await deletePlan({ matchId: selectedDetailId }).unwrap()
      snack.success(res.message || 'Successfully Deleted')
      setSelectedDetailId(null)
    } catch (err: unknown) {
      const msg =
        (err as { data?: { detail?: string } })?.data?.detail ??
        (err as { message?: string })?.message ??
        'Delete failed'
      snack.error(msg)
    }
  }

  const handleReloadAll = () => {
    void pQuery.refetch()
    void sQuery.refetch()
    void detailQuery.refetch()
  }

  // Show centered spinner inside the dialog until the first fetch of P/S/Detail
  // resolves. `isLoading` is only true on the very first fetch — cached opens
  // skip the placeholder. RTK Query refetch (filter change) flips `isFetching`
  // instead, keeping the body visible with inline spinners.
  const isInitialLoading =
    pQuery.isLoading || sQuery.isLoading || detailQuery.isLoading

  /* ---------- render ----------------------------------------------------- */

  return (
    <>
      <DialogTitle
        component="div"
        sx={{ pl: 2, pr: 1, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <Typography
          component="span"
          variant="subtitle1"
          sx={{ fontWeight: 800, fontSize: '0.95rem' }}
        >
          NEW / DELETE DIRECT P-S
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Reload all grids">
          <span>
            <IconButton size="small" onClick={handleReloadAll} disabled={saving || deleting}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {isInitialLoading ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 360,
            }}
          >
            <Stack alignItems="center" spacing={1}>
              <CircularProgress size={32} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
                Loading…
              </Typography>
            </Stack>
          </Box>
        ) : (
          <>
        {!canEdit ? (
          <Alert severity="info" sx={{ py: 0.25 }}>
            Read-only — you don't have edit permission for this module.
          </Alert>
        ) : null}
        {/* Header: Match Date + Note + Save Plan — merged 1 row để tiết kiệm
            ~50px vertical. Helper text move sang Tooltip trên Save button. */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
          sx={{ px: 0.5 }}
        >
          <DatePicker
            label="Match Date"
            format="DD-MMM-YYYY"
            value={matchDate ? dayjs(matchDate) : null}
            onChange={(d) => setMatchDate(d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
            slotProps={{
              textField: {
                size: 'small',
                InputLabelProps: { shrink: true, sx: { fontSize: '0.78rem' } },
                sx: { minWidth: 160, '& .MuiInputBase-input': { fontSize: '0.74rem', py: 0.5 } },
              },
            }}
          />
          <TextField
            label="Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true, sx: { fontSize: '0.78rem' } }}
            sx={{
              flex: 1,
              minWidth: 200,
              '& .MuiInputBase-input': { fontSize: '0.74rem', py: 0.5 },
            }}
          />
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={
              saving ? <CircularProgress size={12} sx={{ color: 'inherit' }} /> : <SaveOutlinedIcon />
            }
            onClick={handleSave}
            disabled={!canEdit || saving}
          >
            Save Plan
          </Button>
        </Stack>

        {/* Hướng dẫn sử dụng — visible inline thay vì giấu trong tooltip. 1 dòng
            caption nhỏ, tổng ~18px. Bao gồm: matching rule (1P×nS / nP×1S),
            autofill rule (tick → KG load Pending), validation visual (red khi
            > Pending — mirror Access ConditionalFormat trên match_kg), và
            delete flow (click row → Delete Plan). */}
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
          sx={{ px: 0.5, color: 'text.secondary' }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 13, color: 'info.main' }} />
          <Typography variant="caption" sx={{ fontSize: '0.68rem', fontStyle: 'italic', lineHeight: 1.3 }}>
            Tick <b>1 Purchase × many Sales</b> or <b>many Purchases × 1 Sale</b> (no multi×multi).
            {' • '}
            Tick → KG(EDIT) auto-loads <b>Pending</b>; edit to allocate partial.
            {' • '}
            KG(EDIT) turns{' '}
            <Box component="span" sx={{ color: '#ED1C24', fontWeight: 700 }}>red</Box>
            {' '}if &gt; Pending (invalid).
            {' • '}
            Click a detail row below → <b>Delete Plan</b>.
          </Typography>
        </Stack>

        {/* 2-column candidate grids — flex:1 1 0 để chia space ngang với Detail
            grid; mỗi panel grow theo dialog height (không max-height cố định). */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 0.5,
            flex: '7 1 0',
            minHeight: 280,
          }}
        >
          <CandidateSide
            side="P"
            dateLabel="Latest Delivery"
            category={categoryP}
            onCategoryChange={setCategoryP}
            range={rangeP}
            onRangeChange={setRangeP}
            categoryOptions={categoryOptions}
            columns={P_CANDIDATE_COLS}
            rows={pRows}
            checked={checkedP}
            onToggle={handleToggleP}
            onToggleAll={handleToggleAllP}
            matchKg={matchKgP}
            onMatchKgChange={handleMatchKgP}
            pendingKey="pPlanPending"
            isFetching={pQuery.isFetching}
          />
          <CandidateSide
            side="S"
            dateLabel="Shipment From"
            category={categoryS}
            onCategoryChange={setCategoryS}
            range={rangeS}
            onRangeChange={setRangeS}
            categoryOptions={categoryOptions}
            columns={S_CANDIDATE_COLS}
            rows={sRows}
            checked={checkedS}
            onToggle={handleToggleS}
            onToggleAll={handleToggleAllS}
            matchKg={matchKgS}
            onMatchKgChange={handleMatchKgS}
            pendingKey="sPlanPending"
            isFetching={sQuery.isFetching}
          />
        </Box>

        <Divider />

        {/* Detail title + backend filter (Grade/DateRange = query) + client
            filter (Search + ▽) + Delete — merged 1 row. */}
        <Stack direction="row" spacing={0.5} alignItems="center" useFlexGap flexWrap="wrap" sx={{ px: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.74rem', whiteSpace: 'nowrap' }}>
            DIRECT P-S DETAILS
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
            ({sortedDetailRows.length.toLocaleString('en-US')}
            {sortedDetailRows.length !== detailRows.length
              ? `/${detailRows.length.toLocaleString('en-US')}`
              : ''})
          </Typography>
          {detailQuery.isFetching ? (
            <CircularProgress size={11} sx={{ ml: 0.5 }} />
          ) : null}
          <Box sx={{ width: 12 }} />
          <TextField
            select
            size="small"
            label="Grade"
            value={categoryD}
            onChange={(e) => setCategoryD(e.target.value)}
            InputLabelProps={{ shrink: true, sx: { fontSize: '0.78rem' } }}
            SelectProps={{ displayEmpty: true }}
            sx={{ minWidth: 120, '& .MuiInputBase-input': { fontSize: '0.7rem', py: 0.4 } }}
          >
            <MenuItem value="">
              <em>(all)</em>
            </MenuItem>
            {categoryOptions.map((opt) => (
              <MenuItem key={opt} value={opt} sx={{ fontSize: '0.74rem' }}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
          <DatePicker
            label="Match Date from"
            format="DD-MMM-YYYY"
            value={rangeD.from ? dayjs(rangeD.from) : null}
            onChange={(d) =>
              setRangeD({ ...rangeD, from: d && d.isValid() ? d.format('YYYY-MM-DD') : '' })
            }
            slotProps={{
              textField: {
                size: 'small',
                InputLabelProps: { shrink: true, sx: { fontSize: '0.78rem' } },
                sx: { minWidth: 150, '& .MuiInputBase-input': { fontSize: '0.7rem', py: 0.4 } },
              },
            }}
          />
          <ArrowRightAltIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
          <DatePicker
            label="to"
            format="DD-MMM-YYYY"
            value={rangeD.to ? dayjs(rangeD.to) : null}
            onChange={(d) =>
              setRangeD({ ...rangeD, to: d && d.isValid() ? d.format('YYYY-MM-DD') : '' })
            }
            slotProps={{
              textField: {
                size: 'small',
                InputLabelProps: { shrink: true, sx: { fontSize: '0.78rem' } },
                sx: { minWidth: 150, '& .MuiInputBase-input': { fontSize: '0.7rem', py: 0.4 } },
              },
            }}
          />
          <FilterButton
            label="Search"
            active={!!searchTermD.trim()}
            onClick={(e) => searchPopoverD.open('search', e)}
          />
          {hasActiveDetailFilters ? (
            <Button
              size="small"
              variant="text"
              color="secondary"
              onClick={clearAllDetailFilters}
              sx={filterBarBtnSx}
            >
              Clear ({activeDetailFilterCount})
            </Button>
          ) : null}
          <Box sx={{ flex: 1 }} />
          <Tooltip
            title={
              selectedDetailId == null
                ? 'Click a row to select, then click Delete Plan.'
                : `Selected ID: ${selectedDetailId}`
            }
          >
            <span>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={
                  deleting ? <CircularProgress size={12} sx={{ color: 'inherit' }} /> : <DeleteOutlineIcon />
                }
                onClick={handleDelete}
                disabled={!canEdit || deleting || selectedDetailId == null}
              >
                Delete Plan
              </Button>
            </span>
          </Tooltip>
        </Stack>

        <Box
          sx={{
            flex: '3 1 0',
            minHeight: 120,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {sortedDetailRows.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                minHeight: 120,
              }}
            >
              <Typography variant="caption" color="text.disabled">
                {detailRows.length === 0
                  ? 'No active Direct P-S plans.'
                  : 'No rows match the current filters.'}
              </Typography>
            </Box>
          ) : (
            <TableVirtuoso
              style={{ height: '100%' }}
              data={sortedDetailRows}
              fixedHeaderContent={() => (
                <TableRow>
                  {DETAIL_COLS.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.align ?? 'left'}
                      onClick={() => toggleSortD(col.key)}
                      style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                      sx={{ ...HEADER_CELL_SX, cursor: 'pointer', userSelect: 'none' }}
                    >
                      {col.label}
                      {getTableSortSuffix(sortStateD, col.key)}
                      {col.filter !== 'none' ? (
                        <ColumnFilterIcon
                          active={columnFiltersD.isActive(col.key)}
                          onClick={(position) => setColPopoverD({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                      ) : null}
                    </TableCell>
                  ))}
                </TableRow>
              )}
              itemContent={(_, row) => (
                <>
                  {DETAIL_COLS.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.align ?? 'left'}
                      style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                      sx={{
                        py: 0.15,
                        px: 0.4,
                        fontSize: '0.62rem',
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      <Box
                        component="span"
                        title={formatDetailCell(row, col) || '-'}
                        sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {formatDetailCell(row, col) || '-'}
                      </Box>
                    </TableCell>
                  ))}
                </>
              )}
              components={{
                Table: (props: any) => (
                  <Table {...props} size="small" stickyHeader sx={{ tableLayout: 'fixed' }} />
                ),
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number(props['data-item-index'])
                  const row = Number.isFinite(idx) ? sortedDetailRows[idx] : null
                  const mid = row ? toNumber((row as Record<string, unknown>).matchId) : null
                  const isSelected = mid != null && selectedDetailId === mid
                  return (
                    <TableRow
                      {...props}
                      hover
                      onClick={() => setSelectedDetailId(mid || null)}
                      sx={{
                        cursor: 'pointer',
                        ...(isSelected && {
                          '& .MuiTableCell-root': {
                            bgcolor: (t: any) => `${alpha(t.palette.primary.main, 0.16)} !important`,
                            fontWeight: 700,
                          },
                        }),
                      }}
                    />
                  )
                },
              }}
            />
          )}
        </Box>

        <FilterPopover
          open={searchPopoverD.state !== null}
          anchorEl={searchPopoverD.state?.anchor ?? null}
          onClose={() => searchPopoverD.close()}
        >
          {searchPopoverD.isOpen('search') && (
            <SearchFilterContent
              value={searchTermD}
              onChange={(v) => setSearchTermD(v)}
              placeholder="ID, contract, supplier/buyer, place, note..."
              onClear={() => searchPopoverD.close()}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopoverD !== null}
          anchorPosition={colPopoverD?.position ?? null}
          onClose={() => setColPopoverD(null)}
          label={popoverDetailColumn?.label ?? ''}
          kind={popoverDetailKind === 'none' ? 'text' : popoverDetailKind}
          value={colPopoverD ? columnFiltersD.filters[colPopoverD.key] : undefined}
          onChange={(v) => {
            if (!colPopoverD) return
            columnFiltersD.setFilter(colPopoverD.key, v)
          }}
          options={popoverDetailOptions}
        />
        </>
        )}
      </DialogContent>
    </>
  )
}

/* ---------- KG(EDIT) cell — memo + local state để fix lag ----------------- */

/**
 * Mỗi keystroke trên KG TextField KHÔNG được gọi parent setState (parent
 * re-render → CandidateSide re-render → TableVirtuoso re-render hết visible
 * rows = ~30-50 TextField MUI mount lại). KgEditCell:
 *   - giữ local state, chỉ commit lên parent khi blur
 *   - validation: chỉ chấp nhận digits (0-9), không cho `-`, `e`, `.`, dấu phẩy
 *   - paste cũng được filter bởi regex check trong onChange
 *   - sync external khi parent reset (vd sau Save Plan setMatchKg({}))
 */
const KgEditCell = memo(function KgEditCell({
  cn,
  pendingValue,
  externalValue,
  width,
  onCommit,
}: {
  cn: string
  pendingValue: unknown
  externalValue: string
  width: number
  onCommit: (cn: string, value: string) => void
}) {
  const [local, setLocal] = useState(externalValue)
  const [focused, setFocused] = useState(false)
  const lastExternalRef = useRef(externalValue)

  useEffect(() => {
    if (externalValue !== lastExternalRef.current) {
      setLocal(externalValue)
      lastExternalRef.current = externalValue
    }
  }, [externalValue])

  // Display: formatted (1,234,567) khi blur — giống cột Pending; plain digits
  // khi focus để cursor không jump giữa string có dấu phẩy. State `local`
  // luôn giữ plain digits, commit lên parent cũng plain → Number(raw) safe.
  const displayed = focused
    ? local
    : local
      ? fmtNumber(Number(local))
      : ''

  // Access ConditionalFormat (form_frmMatchingPSMainSubFormDirect_p.txt + _s)
  // trên field `match_kg`: BOLD + forecolor 0xED1C24 (red) + backcolor white
  // khi value vi phạm rule — Access reject `match_kg > plan_pending` ở save
  // nên đây là điều kiện được flag. Implement equivalent: text đỏ + bold khi
  // user nhập value > pending → visual warning trước khi click Save Plan.
  const numericValue = local === '' ? null : Number(local)
  const pendingNum = toNumber(pendingValue)
  const isOverPending =
    numericValue != null && Number.isFinite(numericValue) && pendingNum > 0 && numericValue > pendingNum

  return (
    <TextField
      size="small"
      type="text"
      inputMode="numeric"
      placeholder={String(fmtNumber(pendingValue) || '')}
      value={displayed}
      onFocus={() => setFocused(true)}
      onChange={(e) => {
        // Strip non-digits từ paste (vd "1,234" → "1234"). Block `-`, `.`,
        // `e`, comma, space.
        const v = e.target.value.replace(/[^\d]/g, '')
        if (v === '' || /^\d+$/.test(v)) setLocal(v)
      }}
      onBlur={() => {
        setFocused(false)
        if (local !== externalValue) {
          lastExternalRef.current = local
          onCommit(cn, local)
        }
      }}
      inputProps={{
        style: {
          fontSize: '0.62rem',
          padding: '1px 3px',
          textAlign: 'right',
          ...(isOverPending && { color: '#ED1C24', fontWeight: 700 }),
        },
      }}
      sx={{
        width: width - 8,
        '& .MuiOutlinedInput-root': { fontSize: '0.62rem' },
      }}
    />
  )
})

/* ---------- candidate side panel ------------------------------------------ */

interface CandidateSideProps {
  side: Side
  dateLabel: string
  category: string
  onCategoryChange: (v: string) => void
  range: DateRange
  onRangeChange: (r: DateRange) => void
  categoryOptions: string[]
  columns: CandidateCol[]
  rows: Record<string, unknown>[]
  checked: Set<string>
  onToggle: (contractNo: string, pending: number) => void
  onToggleAll: (allChecked: boolean) => void
  matchKg: Record<string, string>
  onMatchKgChange: (contractNo: string, value: string) => void
  pendingKey: string
  isFetching: boolean
}

function CandidateSide({
  side,
  dateLabel,
  category,
  onCategoryChange,
  range,
  onRangeChange,
  categoryOptions,
  columns,
  rows,
  checked,
  onToggle,
  onToggleAll,
  matchKg,
  onMatchKgChange,
  pendingKey,
  isFetching,
}: CandidateSideProps) {
  const accent = ACCENT[side]

  // Table-style state (per side). Global Search + per-column ▽ filter + sort,
  // tách biệt với Grade/DateRange ở trên (Grade/DateRange = backend query
  // filter; còn state này = client-side filter trên rows đã load về).
  const [searchTerm, setSearchTerm] = useState('')
  const searchPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const [sortState, setSortState] = useState<TableSortState<string> | null>(null)

  const filteredRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          rowMatchesSearch(r, searchTerm, columns)
          && rowMatchesAllColumnFilters(r, columnFilters.filters),
      ),
    [rows, searchTerm, columnFilters.filters, columns],
  )

  const sortedRows = useMemo(
    () =>
      sortTableRows(
        filteredRows,
        sortState,
        columns as ReadonlyArray<TableSortColumn<string>>,
      ),
    [filteredRows, sortState, columns],
  )

  const allChecked =
    sortedRows.length > 0 && sortedRows.every((r) => checked.has(String(r.contractNo ?? '')))
  const someChecked = sortedRows.some((r) => checked.has(String(r.contractNo ?? '')))

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount =
    (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    searchPopover.close()
  }

  const popoverColumn = useMemo(
    () => (colPopover ? columns.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover, columns],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, rows) : 'text'),
    [popoverColumn, rows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(rows, colPopover.key)
        : undefined,
    [popoverKind, colPopover, rows],
  )

  const toggleSort = (key: string) => setSortState((prev) => toggleTableSort(prev, key))


  return (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderLeftWidth: 3,
        borderLeftColor: accent,
        borderColor: 'divider',
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {/* Single compact toolbar — chip P/S + title + count inline cùng filter
          row, tiết kiệm ~32px banner riêng. bgcolor accent nhạt giữ visual
          marker side P/S. */}
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        useFlexGap
        flexWrap="wrap"
        sx={{ px: 0.5, py: 0.3, bgcolor: alpha(accent, 0.06), borderBottom: 1, borderColor: 'divider' }}
      >
        <Chip
          size="small"
          label={side}
          sx={{ height: 18, bgcolor: accent, color: '#fff', fontWeight: 700, '& .MuiChip-label': { px: 0.75, fontSize: '0.66rem' } }}
        />
        {/* Chip P/S đã đủ identify side → bỏ title text, chỉ giữ count. */}
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, fontSize: '0.7rem', whiteSpace: 'nowrap', color: 'text.secondary' }}
        >
          ({sortedRows.length.toLocaleString('en-US')}
          {sortedRows.length !== rows.length ? `/${rows.length.toLocaleString('en-US')}` : ''})
        </Typography>
        {isFetching ? <CircularProgress size={10} sx={{ ml: 0.5, color: accent }} /> : null}
        <Box sx={{ width: 8 }} />
        <TextField
          select
          size="small"
          label="Grade"
          value={category}
          onChange={(e) => onCategoryChange(e.target.value)}
          InputLabelProps={{ shrink: true, sx: { fontSize: '0.78rem' } }}
          SelectProps={{ displayEmpty: true }}
          sx={{ flex: '1 1 0', minWidth: 90, '& .MuiInputBase-input': { fontSize: '0.68rem', py: 0.35 } }}
        >
          <MenuItem value="">
            <em>(all)</em>
          </MenuItem>
          {categoryOptions.map((opt) => (
            <MenuItem key={opt} value={opt} sx={{ fontSize: '0.72rem' }}>
              {opt}
            </MenuItem>
          ))}
        </TextField>
        <DatePicker
          label={dateLabel}
          format="DD-MMM-YYYY"
          value={range.from ? dayjs(range.from) : null}
          onChange={(d) =>
            onRangeChange({ ...range, from: d && d.isValid() ? d.format('YYYY-MM-DD') : '' })
          }
          slotProps={{
            textField: {
              size: 'small',
              InputLabelProps: { shrink: true, sx: { fontSize: '0.78rem' } },
              sx: { flex: '1 1 0', minWidth: 120, '& .MuiInputBase-input': { fontSize: '0.68rem', py: 0.35 } },
            },
          }}
        />
        <ArrowRightAltIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
        <DatePicker
          label="to"
          format="DD-MMM-YYYY"
          value={range.to ? dayjs(range.to) : null}
          onChange={(d) =>
            onRangeChange({ ...range, to: d && d.isValid() ? d.format('YYYY-MM-DD') : '' })
          }
          slotProps={{
            textField: {
              size: 'small',
              InputLabelProps: { shrink: true, sx: { fontSize: '0.78rem' } },
              sx: { flex: '1 1 0', minWidth: 120, '& .MuiInputBase-input': { fontSize: '0.68rem', py: 0.35 } },
            },
          }}
        />
        <FilterButton
          label="Search"
          active={!!searchTerm.trim()}
          onClick={(e) => searchPopover.open('search', e)}
        />
        {hasActiveFilters ? (
          <Button
            size="small"
            variant="text"
            color="secondary"
            onClick={clearAllFilters}
            sx={filterBarBtnSx}
          >
            Clear ({activeFilterCount})
          </Button>
        ) : null}
      </Stack>

      <Box
        sx={{
          flex: 1,
          minHeight: 200,
          overflow: 'hidden',
        }}
      >
        {sortedRows.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              minHeight: 200,
            }}
          >
            <Typography variant="caption" color="text.disabled">
              {rows.length === 0
                ? 'No candidates with pending > 0.'
                : 'No rows match the current filters.'}
            </Typography>
          </Box>
        ) : (
          <TableVirtuoso
            style={{ height: '100%' }}
            data={sortedRows}
            fixedHeaderContent={() => (
              <TableRow>
                {columns.map((col) => {
                  if (col.kind === 'check') {
                    return (
                      <TableCell
                        key={col.key}
                        padding="checkbox"
                        align="center"
                        style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                        sx={HEADER_CELL_SX}
                      >
                        <Box
                          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            size="small"
                            checked={allChecked}
                            indeterminate={!allChecked && someChecked}
                            onChange={() => onToggleAll(allChecked)}
                            sx={{ p: 0.15 }}
                            aria-label={col.label}
                          />
                        </Box>
                      </TableCell>
                    )
                  }
                  const sortable = col.kind !== 'editKg'
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align ?? 'left'}
                      onClick={sortable ? () => toggleSort(col.key) : undefined}
                      style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                      sx={{
                        ...HEADER_CELL_SX,
                        cursor: sortable ? 'pointer' : 'default',
                        userSelect: 'none',
                      }}
                    >
                      {col.label}
                      {sortable ? getTableSortSuffix(sortState, col.key) : null}
                      {col.filter !== 'none' ? (
                        <ColumnFilterIcon
                          active={columnFilters.isActive(col.key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                      ) : null}
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
            itemContent={(_, row) => {
              const cn = String(row.contractNo ?? '')
              const isChecked = checked.has(cn)
              return (
                <>
                  {columns.map((col) => {
                    if (col.kind === 'check') {
                      return (
                        <TableCell
                          key={col.key}
                          padding="checkbox"
                          align="center"
                          style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                          sx={{ py: 0.1 }}
                        >
                          <Checkbox
                            size="small"
                            checked={isChecked}
                            onChange={() => onToggle(cn, toNumber(row[pendingKey]))}
                            sx={{ p: 0.15 }}
                          />
                        </TableCell>
                      )
                    }
                    if (col.kind === 'editKg') {
                      return (
                        <TableCell
                          key={col.key}
                          style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                          sx={{ py: 0.1, px: 0.3 }}
                        >
                          {isChecked ? (
                            // Lazy mount: chỉ render TextField cho row đã tick →
                            // tránh mount 500 input khi mở dialog với rows lớn.
                            <KgEditCell
                              cn={cn}
                              pendingValue={row[pendingKey]}
                              externalValue={matchKg[cn] ?? ''}
                              width={col.width}
                              onCommit={onMatchKgChange}
                            />
                          ) : null}
                        </TableCell>
                      )
                    }
                    const v = row[col.key]
                    const text =
                      col.kind === 'date'
                        ? fmtDate(v)
                        : col.kind === 'number'
                          ? fmtNumber(v)
                          : v == null
                            ? ''
                            : String(v)
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align ?? 'left'}
                        style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                        sx={{
                          py: 0.15,
                          px: 0.4,
                          fontSize: '0.62rem',
                          lineHeight: 1.2,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        <Box
                          component="span"
                          title={text || '-'}
                          sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          {text || '-'}
                        </Box>
                      </TableCell>
                    )
                  })}
                </>
              )
            }}
            components={{
              Table: (props: any) => (
                <Table {...props} size="small" stickyHeader sx={{ tableLayout: 'fixed' }} />
              ),
              TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
              TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
              TableRow: (props: any) => {
                const idx = Number(props['data-item-index'])
                const row = Number.isFinite(idx) ? sortedRows[idx] : null
                const cn = row ? String((row as Record<string, unknown>).contractNo ?? '') : ''
                const isChecked = checked.has(cn)
                return (
                  <TableRow
                    {...props}
                    hover
                    sx={{
                      ...(isChecked && {
                        '& .MuiTableCell-root': {
                          bgcolor: `${alpha(accent, 0.14)} !important`,
                        },
                      }),
                    }}
                  />
                )
              },
            }}
          />
        )}
      </Box>

      <FilterPopover
        open={searchPopover.state !== null}
        anchorEl={searchPopover.state?.anchor ?? null}
        onClose={() => searchPopover.close()}
      >
        {searchPopover.isOpen('search') && (
          <SearchFilterContent
            value={searchTerm}
            onChange={(v) => setSearchTerm(v)}
            placeholder="Contract no, supplier/buyer, place..."
            onClear={() => searchPopover.close()}
          />
        )}
      </FilterPopover>

      <ColumnFilterPopover
        open={colPopover !== null}
        anchorPosition={colPopover?.position ?? null}
        onClose={() => setColPopover(null)}
        label={popoverColumn?.label ?? ''}
        kind={popoverKind === 'none' ? 'text' : popoverKind}
        value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
        onChange={(v) => {
          if (!colPopover) return
          columnFilters.setFilter(colPopover.key, v)
        }}
        options={popoverOptions}
      />
    </Paper>
  )
}
